import { isLeft } from "fp-ts/lib/Either";
import type { ParsingRule } from "../../types";
import { short } from "diagnostics-lib/io";
import * as io from "io-ts";
import { DateTime } from "luxon";

export const columnsMandatory = [
  ["name", "technicalName"],
  ["formula", "calculatedValueExpression"],
  ["formula", "parameterValue"],
  ["sensorTag", "tagDataSourceImageValue"],
];

export const columnOptionals = [
  ["description"],
  ["unit"],
  ["system", "correspondingSystem", "group"],
  ["equipments", "correspondingEquipments"],
  ["equipmentType", "correspondingEquipmentType"],
  ["minimumValue", "filterMin"],
  ["maximumValue", "filterMax"],
  ["maximumStd", "filterMax"],
  ["startDate"],
  ["startsAt"],
  ["locked"],
  ["hidden", "intermediateCalculation"],
];

export const columns = [...columnsMandatory, ...columnOptionals];

export const MAX_NAME_LENGTH = 100;
export const MAX_UNIT_LENGTH = 100;
export const MAX_FORMULA_LENGTH = 4000;

export const hasInvalidColumns: ParsingRule = (header: string[]) =>
  columnsMandatory.every(columnsM => columnsM.some(col => header?.includes(col))) ? false : true;

export const hasDuplicatedRowByQuantityId: ParsingRule = (quantityNames: (string | undefined)[]) =>
  quantityNames.length > new Set(quantityNames).size;

export const hasInvalidQuantityNameSize = (quantityName: string) =>
  quantityName && quantityName.length > MAX_NAME_LENGTH;

export const hasInvalidUnitSize = (unit: string) => unit && unit.length > MAX_UNIT_LENGTH;

export const hasInvalidFormulaSize = (formula: string) =>
  formula && formula.length > MAX_FORMULA_LENGTH;

export const hasInvalidQuantitySensorTagSize = (sensorTag: string) =>
  sensorTag && isLeft(short(io.string).decode(sensorTag));

export const hasInvalidQuantityNameFormat = (quantityName: string) =>
  quantityName?.search(/^[a-zA-Z0-9_]+$/);

export const hasInvalidDescriptionSize = (description: string) =>
  description && description.length > 500;

export const hasInvalidPairValueFormulaSensor = (sensorTag: string, formula: string) =>
  sensorTag && formula;

export const hasMinSuperiorToMax = (minValue: string, maxValue: string) =>
  minValue &&
  maxValue &&
  !window.isNaN(parseFloat(minValue)) &&
  !window.isNaN(parseFloat(maxValue)) &&
  parseFloat(minValue) >= parseFloat(maxValue);

export const hasFiltersNotFloat = (min: string, max: string, maxStd: string): boolean =>
  (min.length > 0 && window.isNaN(parseFloat(min))) ||
  (max.length > 0 && window.isNaN(parseFloat(max))) ||
  (maxStd.length > 0 && window.isNaN(parseFloat(maxStd)));

export const hasNegativeMaxStd = (maxStd: string) =>
  maxStd && !window.isNaN(parseFloat(maxStd)) && parseFloat(maxStd) < 0;

export const hasFormulaQuantitiesWithMaxStd = (formula: string, maxStd: string) =>
  formula && maxStd;

export const hasInvalidFormatEquipments = (equipments: string) => {
  const items = equipments;
  if (!equipments) return false;
  const itemsRegexp = /^[^,].*[^,]$/;
  const check = itemsRegexp.test(items);
  if (!check) return true;
  return false;
};

export const hasInvalidSensorTagInSource = (sensorsTag: string[], sensorTag: string) =>
  sensorTag && !sensorsTag.includes(sensorTag);

export const hasMissingSensorTagORformula = (formula: string, sensorTag: string) =>
  !formula && !sensorTag;

export const hasFormulaSkippedQuantity = (
  formula: string,
  sensorTag: string,
  ignoredQuantityNames: string[],
  ignoredFormulaNames: string[],
) =>
  formula &&
  !sensorTag &&
  (ignoredQuantityNames.some(q => formula.includes(q)) ||
    ignoredFormulaNames.some(q => formula.includes(q)));

export const hasInvalidLockedField = (locked: string) => {
  if (!locked || locked.trim() === "") return false;
  const trimmedLocked = locked.trim().toLowerCase();
  return trimmedLocked !== "true" && trimmedLocked !== "false";
};

export const hasInvalidHiddenField = (hidden: string) => {
  if (!hidden || hidden.trim() === "") return false;
  const trimmedHidden = hidden.trim().toLowerCase();
  return trimmedHidden !== "true" && trimmedHidden !== "false";
};

export const hasLockedTrueWithoutFormula = (locked: string, formula: string) =>
  locked?.trim().toLowerCase() === "true" && (!formula || formula.trim() === "");

const parseStartDate = (startDate: string) => {
  if (!startDate) return null;
  const trimmed = startDate.trim();
  if (!trimmed) return null;

  const candidates = [
    DateTime.fromISO(trimmed, { zone: "utc" }),
    DateTime.fromFormat(trimmed, "yyyy-MM-dd", { zone: "utc" }),
    DateTime.fromFormat(trimmed, "yyyy-MM-dd HH:mm", { zone: "utc" }),
    DateTime.fromFormat(trimmed, "yyyy-MM-dd HH:mm:ss", { zone: "utc" }),
  ];

  const valid = candidates.find(candidate => candidate.isValid);
  return valid ? valid.toUTC() : null;
};

export const hasInvalidStartDateFormat = (startDate: string) =>
  Boolean(startDate) && parseStartDate(startDate) === null;

export const normalizeStartDateToUTC = (startDate: string) => parseStartDate(startDate);
